import {
	faArrowRightFromBracket,
	faArrowRightToBracket,
	faBoxOpen,
	faBuilding,
	faClipboardList,
	faFileInvoiceDollar,
	faLocationDot,
	faMoon,
	faSunBright,
	faTimes,
	faUser,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { PortalClientDto } from '@supply-mover-2-0/shared';
import { useTheme, useTranslation } from '@supply-mover-2-0/web-ui';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDisplaySettings } from '../hooks/useDisplaySettings';
import { usePortalAuth } from '../hooks/usePortalAuth';
import { useCartStore } from '../stores/cartStore';
import { useLoginModalStore } from '../stores/loginModalStore';
import { useMenuDrawerStore } from '../stores/menuDrawerStore';
import { LanguageSwitcher } from './layout/LanguageSwitcher';

function formatClientAddress(client: PortalClientDto): string | null {
	const parts: Array<string> = [];
	if (client.address1 != null && client.address1.length > 0) {
		parts.push(client.address1);
	}
	if (client.address2 != null && client.address2.length > 0) {
		parts.push(client.address2);
	}
	const cityStateZip: Array<string> = [];
	if (client.city != null && client.city.length > 0) {
		cityStateZip.push(client.city);
	}
	if (client.state != null && client.state.length > 0) {
		cityStateZip.push(client.state);
	}
	let locationPart = cityStateZip.join(', ');
	if (client.zip != null && client.zip.length > 0) {
		locationPart =
			locationPart.length > 0 ? `${locationPart} ${client.zip}` : client.zip;
	}
	if (locationPart.length > 0) {
		parts.push(locationPart);
	}
	return parts.length > 0 ? parts.join(', ') : null;
}

/**
 * Mobile menu drawer triggered by the BottomNav's "Menu" tab.
 *
 * Replaces the desktop UserMenu dropdown on mobile — same content, but
 * presented as a right-side slide-in panel since dropdowns are awkward
 * inside a sticky brand header on small screens. Holds:
 *   - User identity (or Sign In CTA when anon)
 *   - Active client / Change Location (when multi-client)
 *   - Shop / Orders / Invoices / Account (gated by storefront features)
 *   - Language switcher (only renders when multi-language is configured)
 *   - Theme toggle
 *   - Sign out
 *
 * Closes on:
 *   - Backdrop tap
 *   - Close button in the header
 *   - Any nav action inside the drawer
 *   - Escape key
 *   - Browser back button (route change)
 */
export function MenuDrawer(): JSX.Element | null {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const isOpen = useMenuDrawerStore((s) => s.isOpen);
	const close = useMenuDrawerStore((s) => s.close);
	const { isAuthenticated, profile, activeClient, availableClients, logout } =
		usePortalAuth();
	const display = useDisplaySettings();
	const { isDark, toggleTheme } = useTheme();
	const openLoginModal = useLoginModalStore((s) => s.open);
	const setCart = useCartStore((s) => s.setCart);

	// Close on Escape so keyboard users / Capacitor hardware-back can
	// dismiss without hunting for the close button.
	useEffect(() => {
		if (!isOpen) return;
		const onKey = (e: KeyboardEvent): void => {
			if (e.key === 'Escape') close();
		};
		document.addEventListener('keydown', onKey);
		return () => document.removeEventListener('keydown', onKey);
	}, [isOpen, close]);

	if (!isOpen) return null;

	const go = (path: string): void => {
		close();
		navigate(path);
	};

	const handleSignIn = (): void => {
		close();
		openLoginModal();
	};

	const handleSignOut = (): void => {
		close();
		logout();
		setCart(null);
		navigate('/');
		openLoginModal();
	};

	const initials =
		profile != null
			? `${profile.firstName?.charAt(0) ?? ''}${profile.lastName?.charAt(0) ?? ''}`.toUpperCase() ||
				'?'
			: '?';
	const activeAddress =
		activeClient != null ? formatClientAddress(activeClient) : null;
	const hasMultipleClients = availableClients.length > 1;

	return (
		// Drawer root. Mobile-only (`md:hidden`). Full-screen instead of a
		// right-anchored slide-out — the prior 85%-width panel left a thin
		// sliver of catalog peeking on the left that read as a render glitch
		// on cards with sparse menu content (anon visitors only see Sign In
		// + Shop + theme toggle, leaving most of the drawer as empty white
		// space). Going edge-to-edge matches the full-screen treatment we
		// gave the chat panel and keeps the close affordance unambiguous
		// (one X in the header).
		<aside
			className="md:hidden fixed inset-0 z-50 bg-brand-surface flex flex-col pt-safe-top pb-safe-bottom"
			role="dialog"
			aria-modal="true"
			aria-label={t('Menu')}
		>
			{/* Header strip — close button + drawer title. `pt-safe-top` on
			    the parent already insets past the iPhone notch / Dynamic
			    Island; this row is just h-14 below it. */}
			<div className="flex items-center justify-between px-4 h-14 border-b border-gray-100 dark:border-gray-800 shrink-0">
				<span className="text-base font-semibold text-gray-900 dark:text-gray-100">
					{t('Menu')}
				</span>
				<button
					type="button"
					onClick={close}
					className="w-10 h-10 -mr-2 flex items-center justify-center text-brand-muted rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
					aria-label={t('Close menu')}
				>
					<FontAwesomeIcon icon={faTimes} />
				</button>
			</div>

			{/* Scrollable body — identity, optional client picker, links */}
			<div className="flex-1 overflow-y-auto">
				{/* Identity — auth state determines presentation */}
				{isAuthenticated && profile != null ? (
					<div className="px-4 py-3 border-b border-gray-100 dark:border-gray-800">
						<div className="flex items-center gap-3">
							<div className="w-10 h-10 rounded-full bg-portal-accent text-white flex items-center justify-center text-sm font-semibold shrink-0">
								{initials}
							</div>
							<div className="min-w-0 flex-1">
								<div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
									{t(profile.firstName)} {t(profile.lastName)}
								</div>
								<div className="text-xs text-brand-muted truncate">
									{t(profile.email)}
								</div>
							</div>
						</div>
					</div>
				) : (
					<div className="px-4 py-3 border-b border-gray-100 dark:border-gray-800">
						<button
							type="button"
							onClick={handleSignIn}
							className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-portal-accent hover:bg-portal-accent-hover text-white font-semibold text-sm transition-colors min-h-touch"
						>
							<FontAwesomeIcon icon={faArrowRightToBracket} />
							<span>{t('Sign In')}</span>
						</button>
					</div>
				)}

				{/* Active client + Change Location — only when authed */}
				{isAuthenticated && activeClient != null && (
					<div className="border-b border-gray-100 dark:border-gray-800 px-4 py-3">
						<div className="text-[10px] font-semibold text-brand-muted uppercase tracking-wider mb-1">
							{t('Location')}
						</div>
						<div className="flex items-start gap-2">
							<FontAwesomeIcon
								icon={faBuilding}
								className="text-xs text-gray-400 mt-1 shrink-0"
							/>
							<div className="flex-1 min-w-0">
								<div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
									{t(activeClient.name)}
								</div>
								{activeAddress != null && (
									<div className="text-xs text-brand-muted mt-0.5">
										{t(activeAddress)}
									</div>
								)}
							</div>
						</div>
						{hasMultipleClients && (
							<button
								type="button"
								onClick={() => go('/select-client')}
								className="mt-2 inline-flex items-center gap-1.5 text-xs text-portal-accent hover:underline"
							>
								<FontAwesomeIcon icon={faLocationDot} className="text-[10px]" />
								<span>{t('Change Location')}</span>
							</button>
						)}
					</div>
				)}

				{/* Primary nav — Shop / Orders / Invoices / Account, gated
					    by feature flags so storefronts that hide order history
					    or purchasing don't expose dead links. */}
				<nav className="py-1">
					<DrawerLink
						icon={faBoxOpen}
						label={t('Shop')}
						onClick={() => go('/catalog')}
					/>
					{isAuthenticated && display.canViewOrderHistory && (
						<DrawerLink
							icon={faClipboardList}
							label={t('Orders')}
							onClick={() => go('/orders')}
						/>
					)}
					{isAuthenticated && display.canViewInvoices && (
						<DrawerLink
							icon={faFileInvoiceDollar}
							label={t('Invoices')}
							onClick={() => go('/invoices')}
						/>
					)}
					{isAuthenticated && (
						<DrawerLink
							icon={faUser}
							label={t('Account Details')}
							onClick={() => go('/account')}
						/>
					)}
				</nav>

				{/* Settings — language switcher only renders when multiple
					    active languages exist (component self-hides), theme
					    toggle is always relevant. */}
				<div className="border-t border-gray-100 dark:border-gray-800 py-1">
					{/* LanguageSwitcher renders its own dropdown trigger
						    when applicable, otherwise returns null. Wrap so
						    the drawer styling lines up. */}
					<div className="px-2">
						<LanguageSwitcher />
					</div>
					<DrawerLink
						icon={isDark ? faSunBright : faMoon}
						label={isDark ? t('Light Mode') : t('Dark Mode')}
						onClick={toggleTheme}
					/>
				</div>

				{/* Sign out — separate section so it doesn't sit next to
					    everyday nav and get tapped accidentally. */}
				{isAuthenticated && (
					<div className="border-t border-gray-100 dark:border-gray-800 py-1">
						<DrawerLink
							icon={faArrowRightFromBracket}
							label={t('Sign Out')}
							onClick={handleSignOut}
						/>
					</div>
				)}
			</div>
		</aside>
	);
}

interface DrawerLinkProps {
	icon: typeof faUser;
	label: string;
	onClick: () => void;
}

function DrawerLink({ icon, label, onClick }: DrawerLinkProps): JSX.Element {
	return (
		<button
			type="button"
			onClick={onClick}
			className="w-full text-left px-4 py-3 text-sm text-gray-800 dark:text-gray-200 hover:bg-brand-input flex items-center gap-3 min-h-touch"
		>
			<FontAwesomeIcon
				icon={icon}
				className="text-base w-5 text-center text-brand-muted"
			/>
			<span>{label}</span>
		</button>
	);
}
